import { Link } from "react-router-dom";
import styled, { css } from "styled-components";

import { useAdminSession } from "@contexts/admin/AdminSessionProvider";

import { patientDashboardVisitFromAdmin } from "@pages/AdminDashboard/adminRoutes";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";

// On a phone the link and the Button clear the 44px hit target (SHELL-R38, SHELL-R48,
// PHONE-R3); the design's drawer pads Sign out 16px at each side and the row 12px.
const phoneHitTarget = 44;
const phoneSignOutPaddingX = 16;
const phoneRowGap = 12;

const phoneLinkStyle = css`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  min-height: ${phoneHitTarget}px;
`;

// The foot of the sidebar on every rebuilt-admin screen (SHELL-R13): who is signed in,
// the way back to the patient dashboard (XCUT-R19) and sign-out. Look: the design's
// sidebar footer, identity above a Sign out secondary pill.
const Session = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-direction: column;
  gap: ${adminTokens.spacing[1]}px;
  padding: 14px 10px 0;
  border-top: 1px solid ${adminTokens.color.border};
  color: ${adminTokens.color.navy};
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

// The drawer's session row: the admin's name and email, Sign out at its right end (SHELL-R38).
const PhoneSessionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${phoneRowGap}px;

  & > :first-child {
    flex: 1;
  }
`;

const Name = styled.span`
  font-family: ${adminTokens.typeScale.labelValue.fontFamily};
  font-size: ${adminTokens.typeScale.labelValue.fontSize};
`;

const Email = styled.span`
  font-family: ${adminTokens.typeScale.meta.fontFamily};
  font-size: 12px;
  color: ${adminTokens.color.inkSoft};
`;

const PatientDashboardLink = styled(Link)<{ $isPhone: boolean }>`
  font-family: ${adminTokens.typeScale.labelValue.fontFamily};
  font-size: ${adminTokens.typeScale.labelValue.fontSize};
  color: ${adminTokens.color.tealDark};
  text-decoration: underline;
  text-underline-offset: 3px;
  ${({ $isPhone }) => $isPhone && phoneLinkStyle}

  &:hover,
  &:focus-visible {
    color: ${adminTokens.color.navy};
  }

  &:focus-visible {
    ${focusOutline}
    border-radius: ${adminTokens.radius.input}px;
  }
`;

const SignOutButton = styled.button<{ $isPhone: boolean }>`
  align-self: ${({ $isPhone }) => ($isPhone ? "center" : "flex-start")};
  height: ${({ $isPhone }) => ($isPhone ? phoneHitTarget : adminTokens.density.minimumHitTarget)}px;
  flex: none;
  padding: 0 ${({ $isPhone }) => ($isPhone ? phoneSignOutPaddingX : 14)}px;
  border-radius: ${adminTokens.radius.pill}px;
  border: 1px solid ${adminTokens.color.navy};
  background: ${adminTokens.color.surface};
  color: ${adminTokens.color.navy};
  font-family: ${adminTokens.typeScale.labelValue.fontFamily};
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: ${adminTokens.color.navy};
    color: ${adminTokens.color.surface};
  }

  &:focus-visible {
    ${focusOutline}
  }
`;

const AdminSessionBar = () => {
  const { admin, signOut } = useAdminSession();
  const isPhone = useIsPhoneLayout();

  const identity = (
    <Identity>
      <Name>{admin.fullName}</Name>
      <Email>{admin.email}</Email>
    </Identity>
  );
  const patientDashboardLink = (
    <PatientDashboardLink to={patientDashboardVisitFromAdmin} $isPhone={isPhone}>
      Patient dashboard
    </PatientDashboardLink>
  );
  const signOutButton = (
    <SignOutButton type="button" onClick={signOut} $isPhone={isPhone}>
      Sign out
    </SignOutButton>
  );

  if (isPhone) {
    return (
      <Session aria-label="Admin session">
        <PhoneSessionRow>
          {identity}
          {signOutButton}
        </PhoneSessionRow>
        {patientDashboardLink}
      </Session>
    );
  }

  return (
    <Session aria-label="Admin session">
      {identity}
      {patientDashboardLink}
      {signOutButton}
    </Session>
  );
};

export default AdminSessionBar;
